import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import {
  IconCalendarClock,
  IconCertificate,
  IconClipboardCheck,
} from "@tabler/icons-react";
import { AdminPageHeader } from "@/components/admin/admin-page-header";
import { useAuth } from "@/hooks/use-auth";
import { useSelectedSite } from "@/hooks/use-selected-site";

export const Route = createFileRoute("/dashboard/workspace")({
  component: WorkspaceLayout,
  staticData: {
    title: "Workspace",
  },
});

type NavItem = {
  label: string;
  to?: string;
  icon: typeof IconClipboardCheck;
  soon?: boolean;
};

// How the selected site runs. These are site settings, not account settings:
// they belong to the site the user is looking at, and a manager of that site
// changes them. Account settings (profile, security, appearance) live under
// /dashboard/settings and are about the person.
const NAV_ITEMS: NavItem[] = [
  {
    label: "Inspections",
    to: "/dashboard/workspace/inspections",
    icon: IconClipboardCheck,
  },
  {
    label: "Inspection schedule",
    to: "/dashboard/workspace/inspection-schedule",
    icon: IconCalendarClock,
  },
  { label: "Certificates", icon: IconCertificate, soon: true },
];

function WorkspaceLayout() {
  const { site } = useSelectedSite();
  const siteName = site?.name;
  // A manager reaches this page from their own sidebar section, an admin
  // from the Admin section. The badge names the register the viewer is in.
  const { isPlatformAdmin } = useAuth();

  return (
    <div className="p-4 lg:p-7">
      <AdminPageHeader
        className="mb-8"
        badge={isPlatformAdmin ? "Admin" : "Manager"}
        title="Workspace"
        description={
          siteName
            ? `How ${siteName} runs its inspections and keeps its records.`
            : "How the selected site runs its inspections and keeps its records."
        }
      />

      <div className="border-border flex flex-col gap-8 border-t pt-8 md:flex-row md:gap-12">
        <nav
          aria-label="Workspace sections"
          className="flex shrink-0 flex-col gap-1 md:w-52"
        >
          {NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            if (!item.to) {
              return (
                <span
                  key={item.label}
                  aria-disabled="true"
                  className="text-muted-foreground/70 flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm"
                >
                  <Icon className="size-4 shrink-0" aria-hidden="true" />
                  {item.label}
                  {item.soon && (
                    <span className="text-muted-foreground/70 ml-auto text-[11px]">
                      Soon
                    </span>
                  )}
                </span>
              );
            }
            return (
              <Link
                key={item.label}
                to={item.to}
                className="text-muted-foreground hover:bg-muted hover:text-foreground flex items-center gap-2.5 rounded-md px-2.5 py-2 text-sm transition-colors"
                activeProps={{
                  className: "bg-muted text-foreground font-medium",
                }}
              >
                <Icon className="size-4 shrink-0" aria-hidden="true" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="min-w-0 flex-1">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
